import React from 'react'
import {
  FormControl,
  FormControlProps,
  InputLabel,
  outlinedInputClasses,
  styled,
  Typography,
  typographyClasses,
  unstable_composeClasses,
} from '@mui/material'
import classNames from 'classnames'

import { testHandle } from '@acre/utils'

import { FieldBaseProps } from '../Field/Field.types'
import displayFieldClasses, { getDisplayFieldUtilityClass } from './displayFieldClasses'

type FormControlWithoutVariant = Omit<FormControlProps, 'variant'>

export interface DisplayFieldProps extends FieldBaseProps, Omit<FormControlWithoutVariant, 'variant' | 'size'> {
  label?: string | React.ReactNode
  value?: string
  controls?: React.ReactNode
  loading?: boolean
}

const FormControlWrapper = (props: Omit<FormControlWithoutVariant, 'variant' | 'size'>) => (
  <FormControl {...(props as FormControlProps)} />
)

const DisplayFieldBase = styled(FormControlWrapper, {
  name: 'DisplayField',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root, styles[props.variant], styles[props.size]],
  shouldForwardProp: (prop) => prop !== 'controls' && prop !== 'loading' && prop !== 'variant',
})<DisplayFieldProps>(({ controls, disabled, size, theme, variant }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  flexWrap: 'wrap',

  ...(variant === 'horizontal' && {
    flexDirection: 'row',
    alignItems: 'center',
  }),

  ...(variant === 'vertical' && {
    flexDirection: 'column',
    flex: 1,
    alignItems: 'stretch',
    height: '100%',
  }),

  [`& .${displayFieldClasses.label}`]: {
    position: 'static',
    transform: 'none',

    color: theme.colours.base,
    fontSize: 12,
    maxWidth: 'unset',

    [`&.${outlinedInputClasses.focused}`]: {
      color: 'inherit',
    },

    ...(variant === 'horizontal' && {
      minWidth: '30%',
      marginRight: theme.spacing(1),
    }),

    ...(variant === 'vertical' && {
      ...(size === 'large' && {
        fontSize: theme.typography.pxToRem(14),
        lineHeight: '1.2em',
      }),
      marginBottom: '3px',
    }),
  },

  [`& .${typographyClasses.root}`]: {
    display: 'inline-flex',
    position: 'relative',
  },

  [`& > div, & > div > .${typographyClasses.root}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },

  ...(disabled && {
    [`& .${typographyClasses.root}`]: {
      color: theme.colours.baseMid,
    },
  }),

  [`& .${displayFieldClasses.value}`]: {
    ...(variant === 'vertical' && {
      ...(size === 'large' && {
        height: '3rem',
        fontSize: theme.typography.pxToRem(16),
        lineHeight: '3rem',
      }),
    }),
  },

  ...(variant === 'horizontal' &&
    controls === undefined && {
      height: theme.spacing(4),
    }),
}))

const useUtilityClasses = (ownerState: Partial<DisplayFieldProps>) => {
  const slots = {
    root: ['root', ownerState.variant, ownerState.size],
    field: ['field'],
    label: ['label'],
    value: ['value'],
    controls: ['controls'],
  }

  return unstable_composeClasses(slots, getDisplayFieldUtilityClass, ownerState.classes)
}

const DisplayField = (props: DisplayFieldProps) => {
  const {
    classes,
    className,
    label,
    value = '',
    controls,
    name,
    variant = 'horizontal',
    size = 'medium',
    loading,
    ...rootProps
  } = props

  const slotClasses = useUtilityClasses({ classes, size, variant })

  const valueProps = { id: name, component: 'span', 'aria-labelledby': `${name}-label` }

  return (
    <DisplayFieldBase
      data-testid={testHandle(rootProps.id || name)}
      {...rootProps}
      name={name}
      controls={controls}
      variant={variant}
      size={size}
      className={classNames(slotClasses.root, slotClasses.field, className)}
    >
      {label && (
        <InputLabel id={valueProps['aria-labelledby']} className={classNames(slotClasses.label)} htmlFor={name}>
          {label}
        </InputLabel>
      )}
      {controls ? (
        <div className={classNames(slotClasses.controls)}>
          <Typography {...valueProps} className={classNames(slotClasses.value)}>
            {loading ? 'Loading...' : value}
          </Typography>
          {controls}
        </div>
      ) : (
        <Typography {...valueProps} className={classNames(slotClasses.value)}>
          {loading ? 'Loading...' : value}
        </Typography>
      )}
    </DisplayFieldBase>
  )
}

export default DisplayField
